import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { useCallback } from 'react';

interface LayoutProps {
    children: ReactNode;
    /**
     * When true, main has zero padding (CPM pages own their own 28px page padding).
     * Also hides the empty footer so bottom inset matches Counter Flow.
     */
    flush?: boolean;
    /** Hide the Admin Dashboard bar so a page can own the top chrome (editor screens). */
    hideHeader?: boolean;
}

/**
 * Main layout component following BottleShop design system
 * Includes header with navigation and footer
 */
export default function Layout({ children, flush = false, hideHeader = false }: LayoutProps) {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const handleLogoClick = useCallback(() => {
        navigate('/');
    }, [navigate]);

    return (
        <div className="min-h-screen flex flex-col bg-[#fafbfc]">
            {hideHeader ? null : (
                <header className="sticky top-0 z-50 w-full bg-white border-b border-[#e2e8f0] shadow-sm">
                    <div className="w-full px-6">
                        <div className="h-16 flex items-center justify-between">
                            {/* Logo */}
                            <button
                                onClick={handleLogoClick}
                                className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-150"
                                aria-label="Go to dashboard"
                            >
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#6e64f0] to-[#8b7cf6] flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold text-lg leading-none">
                                        B
                                    </span>
                                </div>
                                <h1 className="text-sm font-semibold text-[#1e293b] leading-none">
                                    Admin Dashboard
                                </h1>
                            </button>

                            {/* Spacer */}
                            <div className="flex-1"></div>

                            {/* Actions */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={logout}
                                    className="flex items-center gap-2 px-4 h-10 rounded-lg hover:bg-[#f1f5f9] transition-colors duration-150 text-[#64748b] hover:text-[#1e293b]"
                                    aria-label="Logout"
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">Logout</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </header>
            )}

            {/* Main Content — flush=true for CPM (page owns padding) */}
            <main className={flush ? 'flex-1 w-full p-0' : 'flex-1 w-full px-6 py-8'}>
                {children}
            </main>

            {!flush ? (
                <footer className="w-full bg-white border-t border-[#e2e8f0] mt-auto">
                    <div className="w-full px-6 py-4">{/* Empty footer for now */}</div>
                </footer>
            ) : null}
        </div>
    );
}
